import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    Select,
    Products,
    PROVINCE,
    Typography,
    useIsMounted,
    LegacyCityPropType,
    MessagePropType,
    AnchorAnimation,
    LocationSelector,
    getAffiliateData,
    getLanguageFromLocale,
    AFFILIATE_QUERY_PARAM_KEYS,
} from '@ratehub/base-ui';
import {
    CHEQUING_TYPES,
    CHEQUING_TYPES_OPTIONS,
    CHEQUING_MONTHLY_BALANCES,
    CHEQUING_MONTHLY_BALANCES_OPTIONS,
    CHEQUING_TRANSACTIONS,
    CHEQUING_TRANSACTIONS_OPTIONS,
} from '@ratehub/inv-common';

import PAGE_TEMPLATES from '../definitions/PageTemplates';
import ChequingAccountPropType from '../definitions/ChequingAccountPropType';
import fetchProductsForChequing from '../functions/fetchProductsForChequing';
import useTrackSponsoredProductsHeapEvent from '../hooks/useTrackSponsoredProductsHeapEvent';
import ProductList from './ProductList';
import ProductCardLayoutChequing from './ProductCardLayoutChequing';


function ProductListChequing({
    title,

    initialAccountType = CHEQUING_TYPES.ALL,
    initialMonthlyBalance = CHEQUING_MONTHLY_BALANCES[0],        // $500
    initialTransactions = CHEQUING_TRANSACTIONS[3],              // 15
    initialProvider,

    initialCity,

    initialProducts = [],
    lastUpdated,

    maxRowsCollapsed,
    maxRowsExpanded,

    ...otherProps
}) {
    const intl = useIntl();
    const getIsMounted = useIsMounted();

    // TODO: remove workaround after LocationSelector fixed for FilteredAsyncContainer
    // see https://ratehub.atlassian.net/browse/CCDEP-3541
    const [ provinceCode, setProvinceCode ] = useState(initialCity?.province.code || PROVINCE.ONTARIO);
    function updateLocation(values) {
        if (values?.province?.code) {
            setProvinceCode(values.province.code);
        }
    }

    const [ lastUpdatedDate, setLastUpdatedDate ] = useState(lastUpdated);

    // used to track sponsored chequing accounts onload
    useTrackSponsoredProductsHeapEvent(initialProducts, Products.CHEQUING);

    const language = getLanguageFromLocale(intl.locale);

    const initialFilterValues = {
        accountType: initialAccountType,
        monthlyBalance: initialMonthlyBalance,
        transactions: initialTransactions,
        provider: initialProvider,
        provinceCode: provinceCode,
        language: language,
    };

    function getFormValuesForFetch(values) {
        return {
            ...initialFilterValues,
            accountType: values.accountType,
            monthlyBalance: values.monthlyBalance,
            transactions: values.transactions,
            provinceCode: values.location?.province.code,
        };
    }


    async function fetchProductChequing(values) {
        const chequingParams = {
            ...values,
            isPrioritizeSponsored: true,
            customId: getAffiliateData()?.[AFFILIATE_QUERY_PARAM_KEYS.affiliateId],
        };

        const response = await fetchProductsForChequing(chequingParams);
        if (response) {
            if (getIsMounted()) {
                setLastUpdatedDate(response.lastUpdated);
            }
        }

        return response.products;
    }


    return (
        <ProductList
            {...otherProps}
            title={title}
            lastUpdated={lastUpdatedDate}

            maxRowsCollapsed={maxRowsCollapsed}
            maxRowsExpanded={maxRowsExpanded}
            noMatchingItemsMessage={(
                <FormattedMessage
                    id="web-components.chequing-product-list.no-matches.label"
                    defaultMessage="No chequing accounts match the search filters."
                />
            )}

            initialFilterValues={initialFilterValues}
            primaryFilters={getChequingFilters({
                initialAccountType,
                initialMonthlyBalance,
                initialTransactions,
                initialCity,
                updateLocation,
            })}
            initialFetchedProducts={initialProducts}
            getFormValues={getFormValuesForFetch}
            onFetchValue={fetchProductChequing}
        >
            {(chequingAccount, formValues) => {
                return (
                    <ProductCardLayoutChequing
                        chequingAccount={chequingAccount}
                        provinceCode={formValues.provinceCode}
                    />
                );
            }}
        </ProductList>
    );
}

ProductListChequing.propTypes = {
    title: MessagePropType.isRequired,

    initialAccountType: PropTypes.oneOf(Object.values(CHEQUING_TYPES)),
    initialMonthlyBalance: PropTypes.number,
    initialTransactions: PropTypes.number,
    initialProvider: PropTypes.string,

    initialCity: LegacyCityPropType,

    initialProducts: PropTypes.arrayOf(ChequingAccountPropType),
    lastUpdated: PropTypes.string,

    maxRowsCollapsed: PropTypes.number,
    maxRowsExpanded: PropTypes.number,
};


function getChequingFilters({
    initialAccountType,
    initialMonthlyBalance,
    initialTransactions,
    initialCity,
    updateLocation,
}) {
    return [
        <Select
            key="accountType"
            name="accountType"
            value={initialAccountType}
            options={CHEQUING_TYPES_OPTIONS}
            label={(
                <FormattedMessage
                    id="web-components.chequing-product-list.filters.accountType.label"
                    defaultMessage="Type"
                />
            )}
            type="dropdown"
            className="filter-question-compact"
        />,

        <Select
            key="monthlyBalance"
            name="monthlyBalance"
            value={initialMonthlyBalance}
            options={CHEQUING_MONTHLY_BALANCES_OPTIONS}
            label={(
                <FormattedMessage
                    id="web-components.chequing-product-list.filters.balance.label"
                    defaultMessage="Balance"
                />
            )}
            type="dropdown"
            className="filter-question-compact"
        />,

        <Select
            key="transactions"
            name="transactions"
            value={initialTransactions}
            options={CHEQUING_TRANSACTIONS_OPTIONS}
            label={(
                <FormattedMessage
                    id="web-components.chequing-product-list.filters.transactions.label"
                    defaultMessage="Transactions"
                />
            )}
            type="dropdown"
            className="filter-question-compact"
        />,

        <LocationSelector
            name="location"
            key="location"
            initialProvince={initialCity?.province.code}
            label={(
                <FormattedMessage
                    id="web-components.chequing-product-list.location.label"
                    defaultMessage="Location"
                />
            )}
            className="filter-question-compact"
            errorMessageStyle="minimal"
            animationType="none"
            onChange={updateLocation}
        />,
    ];
}

ProductListChequing.blockKey = 'rh/product-list-chequing';
ProductListChequing.requiresLayoutRow = true;
ProductListChequing.supportedPageTypes = [ PAGE_TEMPLATES.chequing ];

export default styled(observer(ProductListChequing))`
    // class name from getChequingAccountProps()
    .other-info {
        h3,
        h4 {
            font-weight: ${Typography.WEIGHTS.MEDIUM};
        }

        a {
            ${AnchorAnimation};
        }
    }
`;
